export const auditDashboardTheme = {
  colors: {
    surface: "#ffffff",
    surface2: "#fcfbff",
    surface3: "#f8f9ff",
    tableHeader: "#f3f2f7",
    line: "#dee2ee",
    lineStrong: "#C8CEE3",
    lineSoft: "rgba(163, 174, 208, 0.24)",
    text: "#222b45",
    textMutedStrong: "#222b4599",
    description: "rgba(34, 43, 69, 0.46)",
    blue: "#2c6ca0",
    blueSoft: "#e8f7fd",
    blueTint: "#f5f9fc",
    red: "#ff0000",
    redSoft: "#fff1f1",
    green: "#34c759",
    greenSoft: "#effaf3",
    amber: "#df9400",
    amberSoft: "#fff7ea",
    shadow: "0 0 0 1px rgba(0, 0, 0, 0.02), 0 8px 22px rgba(34, 43, 69, 0.06)",
    containerBorder: "#C8CEE3",
    headerPositive: "#EFF6FF",
    headerNegative: "#FEF2F2",
    headerMatched: "#F0FDF4",
    headerProgress: "#FFF7ED",
    headerDefault: "#f0f2f2",
    textPositive: "#0000ff",
    textNegative: "#ff0000",
    textMatched: "#34c759",
    textProgress: "#B45309",
    textDefault: "#222b4599",
  },
  shape: {
    containerRadius: 12,
  },
  layout: {
    stickyTop: {
      xs: 72,
      md: 78,
    },
    stickyHeightOffset: {
      md: 88,
    },
  },
};

export type AuditTone = "positive" | "negative" | "matched" | "progress" | "default";

export const auditToneStyles: Record<AuditTone, { bg: string; border: string; text: string }> = {
  positive: {
    bg: auditDashboardTheme.colors.headerPositive,
    border: "#60A5FA",
    text: auditDashboardTheme.colors.textPositive,
  },
  negative: {
    bg: auditDashboardTheme.colors.headerNegative,
    border: "#F87171",
    text: auditDashboardTheme.colors.textNegative,
  },
  matched: {
    bg: auditDashboardTheme.colors.headerMatched,
    border: "#4ADE80",
    text: auditDashboardTheme.colors.textMatched,
  },
  progress: {
    bg: auditDashboardTheme.colors.headerProgress,
    border: "#FDBA74",
    text: auditDashboardTheme.colors.textProgress,
  },
  default: {
    bg: auditDashboardTheme.colors.headerDefault,
    border: "#94A3B8",
    text: auditDashboardTheme.colors.textDefault,
  },
};

export type AuditSummaryComparisonVariant = "range" | "detail";

export const getAuditSummaryComparisonStyles = (variant: AuditSummaryComparisonVariant) => {
  const isRangeSummary = variant === "range";
  const gridColumns = isRangeSummary
    ? "minmax(82px, 1fr) minmax(66px, 0.72fr) minmax(74px, 0.76fr) minmax(66px, 0.7fr)"
    : "minmax(0, 1fr) minmax(96px, 0.72fr) minmax(108px, 0.78fr) minmax(84px, 0.62fr)";

  return {
    gridColumns,
    splitColumnLabels: isRangeSummary,
    showFirstRowDivider: isRangeSummary,
    showHelperText: !isRangeSummary,
    showDetailNotes: !isRangeSummary,
    showTrailingNote: isRangeSummary,
    matchedIconSize: isRangeSummary ? 14 : 16,
    header: {
      display: "grid",
      gridTemplateColumns: gridColumns,
      columnGap: 1,
      alignItems: "center",
      minHeight: isRangeSummary ? undefined : 36,
      px: isRangeSummary ? 0 : 1.5,
      py: isRangeSummary ? 0 : "8px",
      pb: isRangeSummary ? "6px" : "8px",
      borderBottom: isRangeSummary ? "none" : "1px solid",
      borderColor: auditDashboardTheme.colors.line,
      bgcolor: isRangeSummary ? "transparent" : "rgba(255, 255, 255, 0.5)",
      color: auditDashboardTheme.colors.text,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      mb: isRangeSummary ? 0 : "4px",
    },
    columnHeader: {
      fontSize: 12,
      fontWeight: 800,
      lineHeight: isRangeSummary ? 1.2 : 1.15,
      textAlign: "right",
      whiteSpace: isRangeSummary ? "normal" : undefined,
    },
    rowContainer: {
      px: isRangeSummary ? 0 : 1.5,
      py: isRangeSummary ? 0 : "4px",
      borderBottom: "none",
    },
    row: {
      display: "grid",
      gridTemplateColumns: gridColumns,
      columnGap: 1,
      alignItems: isRangeSummary ? "center" : "start",
      py: isRangeSummary ? "4px" : 0,
      minWidth: 0,
    },
    rowHeader: {
      fontSize: isRangeSummary ? 13 : 14,
      fontWeight: 500,
      lineHeight: isRangeSummary ? 1.3 : 1.35,
      color: auditDashboardTheme.colors.text,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    helperText: {
      mt: 0.25,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.35,
      color: auditDashboardTheme.colors.description,
    },
    valueCell: {
      fontSize: isRangeSummary ? 13 : 14,
      fontWeight: isRangeSummary ? 500 : 400,
      lineHeight: isRangeSummary ? 1.3 : 1.35,
      textAlign: "right",
      color: auditDashboardTheme.colors.text,
      whiteSpace: "nowrap",
      wordBreak: "break-word",
      minWidth: 0,
      justifySelf: "end",
      fontVariantNumeric: "tabular-nums",
    },
    varianceCell: {
      fontWeight: 500,
      fontSize: isRangeSummary ? 13 : 15,
      lineHeight: isRangeSummary ? 1.3 : 1.35,
      whiteSpace: "nowrap",
      wordBreak: "break-word",
      minWidth: 0,
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
    },
    detailNotes: {
      minWidth: 0,
      px: isRangeSummary ? 0 : 1.5,
      pt: "4px",
      pb: "6px",
    },
    detailNoteText: {
      minWidth: 0,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.35,
      color: auditDashboardTheme.colors.description,
      whiteSpace: "normal",
      overflowWrap: "anywhere",
      textAlign: "left",
    },
    trailingNote: {
      display: "flex",
      justifyContent: "flex-start",
      minWidth: 0,
      mt: "3px",
      px: 0,
      pt: "7px",
      pb: "3px",
      borderTop: "2px solid",
      borderBottom: "none",
      borderColor: auditDashboardTheme.colors.lineStrong,
    },
    trailingNoteText: {
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontSize: 11,
      fontStyle: "italic",
      lineHeight: 1.3,
      color: auditDashboardTheme.colors.textMutedStrong,
      fontWeight: 400,
      whiteSpace: "nowrap",
      textAlign: "left",
    },
    trailingNoteLabel: {
      color: auditDashboardTheme.colors.textMutedStrong,
      fontStyle: "normal",
      fontWeight: 700,
    },
  } as const;
};

export const getAuditTone = (variance?: number | null): AuditTone => {
  if (variance == null || Number.isNaN(variance)) {
    return "default";
  }

  if (variance > 0) {
    return "positive";
  }

  if (variance < 0) {
    return "negative";
  }

  return "matched";
};
